import { LocalStorageKey } from "@/utils/local-storage-utils";
import { QueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";

export const IMPERSONATION_COOKIE_PATH = "/";
export const IMPERSONATION_COOKIE_NAME = "chalk_impersonated_team";
const IMPERSONATION_COOKIE_CHANGE_EVENT = "chalk-impersonation-cookie-change";

export function subscribeToImpersonationCookie(onChange: () => void): () => void {
  const onStorage = (event: StorageEvent) => {
    if (event.key === LocalStorageKey.IMPERSONATION_COOKIE_CHANGE) onChange();
  };
  const onVisibilityChange = () => {
    if (document.visibilityState === "visible") onChange();
  };
  window.addEventListener(IMPERSONATION_COOKIE_CHANGE_EVENT, onChange);
  window.addEventListener("storage", onStorage);
  // Focus/visibility also catch cookie changes from other origins or when storage is blocked.
  window.addEventListener("focus", onChange);
  document.addEventListener("visibilitychange", onVisibilityChange);
  return () => {
    window.removeEventListener(IMPERSONATION_COOKIE_CHANGE_EVENT, onChange);
    window.removeEventListener("storage", onStorage);
    window.removeEventListener("focus", onChange);
    document.removeEventListener("visibilitychange", onVisibilityChange);
  };
}

function notifyImpersonationCookieChange() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(IMPERSONATION_COOKIE_CHANGE_EVENT));
    try {
      // A unique signal notifies other same-origin tabs; the cookie remains the source of truth.
      window.localStorage.setItem(
        LocalStorageKey.IMPERSONATION_COOKIE_CHANGE,
        `${Date.now()}:${Math.random()}`,
      );
    } catch {
      // Cookie updates still succeed when storage is unavailable; tabs recheck on activation.
    }
  }
}

export async function setImpersonationCookieBackend(
  projectId: string | undefined,
): Promise<boolean> {
  if (!projectId) return false;
  try {
    const response = await fetch(`/api/project-info`, {
      method: "POST",
      body: JSON.stringify({ projectId }),
      headers: { "Content-Type": "application/json" },
    });
    if (response.ok) {
      notifyImpersonationCookieChange();
    }
    return response.ok;
  } catch (e) {
    console.error("Failed to fetch project and team cookie", e);
    return false;
  }
}

export function deleteGenericImpersonationCookie() {
  Cookies.remove(IMPERSONATION_COOKIE_NAME, {
    path: IMPERSONATION_COOKIE_PATH,
  });
  notifyImpersonationCookieChange();
}

export function deleteCurrentDomainImpersonationCookies() {
  if (typeof window === "undefined") return;

  const domainLabels = window.location.hostname.split(".");
  const domains = domainLabels.slice(0, -1).map((_, index) => domainLabels.slice(index).join("."));

  for (const domain of domains) {
    Cookies.remove(IMPERSONATION_COOKIE_NAME, {
      domain,
      path: IMPERSONATION_COOKIE_PATH,
    });
  }
  notifyImpersonationCookieChange();
}

export function deleteAllImpersonationCookies() {
  deleteGenericImpersonationCookie();
  deleteCurrentDomainImpersonationCookies();
}

export async function refetchGlobalQueries(grpcQueryClient: QueryClient) {
  grpcQueryClient.refetchQueries();
}

export async function delayedRefetchQueries(grpcQueryClient: QueryClient) {
  setTimeout(async () => {
    await refetchGlobalQueries(grpcQueryClient);
  }, 500);
}

export async function fixImpersonationCookies(
  grpcQueryClient: QueryClient,
  projectId: string | undefined,
): Promise<boolean> {
  deleteAllImpersonationCookies();
  const didSetCookie = await setImpersonationCookieBackend(projectId);
  await delayedRefetchQueries(grpcQueryClient);
  return didSetCookie;
}
